import type {
  HistoricalPageResult,
  HistoricalRow,
  HistoricalRun,
} from '../../consumer/historical/contract';
import { HISTORICAL_METHODS } from '../../consumer/historical/contract';
type Validate = (v: unknown) => void;
const invalid = (): never => {
  throw new Error('Invalid HistoricalForecastV1 response');
};
const text: Validate = (v) => {
  if (typeof v !== 'string' || v.length > 131072) invalid();
};
const nonempty: Validate = (v) => {
  text(v);
  if (!v) invalid();
};
const finite: Validate = (v) => {
  if (typeof v !== 'number' || !Number.isFinite(v)) invalid();
};
const count: Validate = (v) => {
  finite(v);
  if (!Number.isSafeInteger(v) || (v as number) < 0) invalid();
};
const bool: Validate = (v) => {
  if (typeof v !== 'boolean') invalid();
};
const time: Validate = (v) => {
  nonempty(v);
  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(v as string) ||
    !Number.isFinite(Date.parse(v as string))
  )
    invalid();
};
const hash: Validate = (v) => {
  if (typeof v !== 'string' || !/^[a-f0-9]{40}(?:[a-f0-9]{24})?$/.test(v)) invalid();
};
const nullable =
  (f: Validate): Validate =>
  (v) => {
    if (v !== null) f(v);
  };
const one =
  (...values: unknown[]): Validate =>
  (v) => {
    if (!values.includes(v)) invalid();
  };
const array =
  (f: Validate, max = 10000): Validate =>
  (v) => {
    if (!Array.isArray(v) || v.length > max) invalid();
    (v as unknown[]).forEach(f);
  };
const record =
  (shape: Record<string, Validate>): Validate =>
  (v) => {
    if (!v || typeof v !== 'object' || Array.isArray(v)) invalid();
    const o = v as Record<string, unknown>;
    if (
      Object.keys(o).length !== Object.keys(shape).length ||
      Object.keys(o).some((k) => !Object.hasOwn(shape, k))
    )
      invalid();
    for (const [k, f] of Object.entries(shape)) f(o[k]);
  };
const horizon = one(1, 3, 6);
const method = one(...HISTORICAL_METHODS);
const split = one('TRAIN', 'VALIDATION', 'HELD_OUT');
const identity = {
  stationId: nonempty,
  metric: one('TEMPERATURE', 'WIND_SPEED'),
  unit: one('degC', 'm/s'),
  horizonHours: horizon,
  method,
  split,
};
const row = record({
  ...identity,
  origin: time,
  targetAt: time,
  inputStart: nullable(time),
  inputEnd: nullable(time),
  predicted: nullable(finite),
  actual: nullable(finite),
  status: one('EVALUATED', 'UNAVAILABLE_INPUT', 'MISSING_TARGET'),
  reason: nullable(text),
  paired: bool,
});
const group = record({
  ...identity,
  totalOrigins: count,
  generated: count,
  evaluable: count,
  pairedCount: count,
  mae: nullable(finite),
  rmse: nullable(finite),
  bias: nullable(finite),
  coverage: nullable(finite),
  pairedMAE: nullable(finite),
  pairedRMSE: nullable(finite),
  baselineMAEDelta: nullable(finite),
  baselineRMSEDelta: nullable(finite),
  unavailableReasons: array(record({ reason: nonempty, count })),
});
const dataset = record({
  datasetId: nonempty,
  datasetRevision: nonempty,
  sourceType: one(
    'NOAA_GHCNH_STATION_OBSERVATION',
    'SYNTHETIC_OBSERVATIONS',
    'METEOROLOGICAL_REANALYSIS',
    'UNKNOWN_SOURCE',
  ),
  license: text,
  doi: text,
  accessedAt: time,
  snapshotHash: hash,
  start: time,
  end: time,
  resolution: nonempty,
  metrics: array(nonempty, 20),
  sources: array(
    record({
      stationId: nonempty,
      name: text,
      latitude: nullable(finite),
      longitude: nullable(finite),
      url: nonempty,
      originalHash: hash,
      bytes: count,
      downloadedRows: count,
      qualifiedRows: count,
      missingRows: count,
    }),
    100,
  ),
  qualityNotes: array(text),
});
const run = record({
  schemaVersion: one('GHCNH_HOURLY_OFFLINE_V1'),
  runId: (v) => {
    if (typeof v !== 'string' || !/^historical-[a-f0-9]{64}$/.test(v)) invalid();
  },
  status: one('COMPLETE', 'RUNNING', 'FAILED', 'INTERRUPTED'),
  mode: one('RETROSPECTIVE_HINDCAST'),
  createdAt: time,
  dataset,
  split: record({
    trainStart: time,
    trainEnd: time,
    validationStart: time,
    validationEnd: time,
    heldoutStart: time,
    heldoutEnd: time,
    embargoHours: count,
  }),
  freeze: record({
    modelFreezeSHA: hash,
    algorithmSHA: hash,
    trainingHash: hash,
    parametersJson: text,
    frozenAt: time,
  }),
  horizonsHours: array(horizon, 3),
  methods: array(method, 4),
  predictiveUtility: nonempty,
  groups: array(group, 1000),
  errorCases: array(row, 1000),
  rowsHash: hash,
  rowCount: count,
  limitations: array(text),
});
function validateRow(r: HistoricalRow) {
  if (
    r.unit !== (r.metric === 'TEMPERATURE' ? 'degC' : 'm/s') ||
    Date.parse(r.targetAt) - Date.parse(r.origin) !== r.horizonHours * 3600000 ||
    (r.inputEnd && Date.parse(r.inputEnd) > Date.parse(r.origin)) ||
    (r.inputStart && r.inputEnd && Date.parse(r.inputStart) > Date.parse(r.inputEnd)) ||
    (r.status === 'EVALUATED' && (r.predicted === null || r.actual === null)) ||
    (r.paired && r.status !== 'EVALUATED')
  )
    invalid();
}
export function parseHistoricalRun(v: unknown): HistoricalRun {
  run(v);
  const r = v as HistoricalRun;
  if (
    r.horizonsHours.join(',') !== '1,3,6' ||
    r.methods.join(',') !== HISTORICAL_METHODS.join(',') ||
    Date.parse(r.split.trainEnd) > Date.parse(r.split.validationStart) ||
    Date.parse(r.split.validationEnd) > Date.parse(r.split.heldoutStart)
  )
    invalid();
  r.groups.forEach((g) => {
    if (
      g.unit !== (g.metric === 'TEMPERATURE' ? 'degC' : 'm/s') ||
      g.evaluable > g.generated ||
      g.generated > g.totalOrigins ||
      g.pairedCount > g.evaluable ||
      (g.coverage !== null && (g.coverage < 0 || g.coverage > 1)) ||
      [g.mae, g.rmse, g.pairedMAE, g.pairedRMSE].some((n) => n !== null && n < 0)
    )
      invalid();
  });
  r.errorCases.forEach(validateRow);
  return r;
}
function page<T>(v: unknown, validate: Validate): HistoricalPageResult<T> {
  record({ items: array(validate, 100), total: count, offset: count, limit: count })(v);
  const p = v as HistoricalPageResult<T>;
  if (
    p.limit < 1 ||
    p.limit > 100 ||
    p.items.length > p.limit ||
    p.items.length > Math.max(0, p.total - p.offset)
  )
    invalid();
  return p;
}
export function parseHistoricalRuns(v: unknown): HistoricalPageResult<HistoricalRun> {
  const p = page<HistoricalRun>(v, run);
  p.items.forEach(parseHistoricalRun);
  return p;
}
export function parseHistoricalRows(v: unknown): HistoricalPageResult<HistoricalRow> {
  const p = page<HistoricalRow>(v, row);
  p.items.forEach(validateRow);
  return p;
}
